import { NavLink } from 'react-router-dom';
import { Home, MessageSquare, FileCheck, Building2, Tag, Calendar, TrendingUp, HelpCircle, Sparkles, LayoutDashboard, Bot, Store, Briefcase } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { canUseVendorPortal } from '@/utils/travelBusiness';

const navItems: Array<{
  to: string;
  icon: typeof Home;
  label: string;
  requiresAuth?: boolean;
  requiresVendor?: boolean;
}> = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', requiresAuth: true },
  { to: '/ai-assistant', icon: Bot, label: 'AI Assistant' },
  { to: '/forums', icon: MessageSquare, label: 'Forums' },
  { to: '/visa-experiences', icon: FileCheck, label: 'Visa Experiences' },
  { to: '/directory', icon: Building2, label: 'Directory' },
  { to: '/deals', icon: Tag, label: 'Deals' },
  { to: '/offers', icon: Store, label: 'Offers' },
  { to: '/vendor', icon: Briefcase, label: 'Vendor portal', requiresVendor: true },
  { to: '/events', icon: Calendar, label: 'Events' },
  { to: '/pricing', icon: Sparkles, label: 'Pricing' },
];

const quickLinks = [
  { to: '/forums?sort=trending', icon: TrendingUp, label: 'Trending' },
  { to: '/help', icon: HelpCircle, label: 'Help Center' },
];

export function Sidebar() {
  const { user } = useAuthStore();

  const filteredNavItems = navItems.filter((item) => {
    if (item.requiresVendor) return canUseVendorPortal(user);
    if (item.requiresAuth) return Boolean(user);
    return true;
  });

  return (
    <aside className="hidden lg:block fixed left-0 top-16 w-64 h-[calc(100vh-4rem)] bg-gradient-to-b from-wine-50 to-white border-r border-wine-100 overflow-y-auto">
      <nav className="p-4">
        <div className="space-y-1">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-wine-700 to-wine-600 text-white font-medium shadow-lg shadow-wine-700/25'
                    : 'text-wine-800 hover:bg-wine-100'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
              {item.to === '/ai-assistant' && (
                <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded">AI</span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-wine-200">
          <h3 className="px-4 text-xs font-semibold text-wine-600 uppercase tracking-wider mb-2">
            Quick Links
          </h3>
          <div className="space-y-1">
            {quickLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="flex items-center space-x-3 px-4 py-2 text-wine-700 hover:bg-wine-100 rounded-lg"
              >
                <item.icon className="h-4 w-4" />
                <span className="text-sm">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="mt-8 p-4 bg-gradient-to-br from-wine-800 via-wine-700 to-wine-600 rounded-xl text-white shadow-xl relative overflow-hidden">
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-gold-400 rounded-full flex items-center justify-center">
            <span className="text-wine-900 text-xs font-bold">✨</span>
          </div>
          <h3 className="font-semibold mb-2">Share Your Story</h3>
          <p className="text-sm text-wine-200 mb-3">
            Help fellow travelers by sharing your visa experience
          </p>
          <NavLink
            to="/visa-experiences/create"
            className="block w-full text-center bg-gradient-to-r from-gold-400 to-gold-500 text-wine-900 px-4 py-2 rounded-lg text-sm font-semibold hover:from-gold-300 hover:to-gold-400 transition-all shadow-lg"
          >
            Share Experience
          </NavLink>
        </div>
      </nav>
    </aside>
  );
}
